import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Loader2, RefreshCcw, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const passwordRuleItems = [
  'Au moins 8 caracteres',
  'Une majuscule',
  'Une minuscule',
  'Un chiffre',
];

const hasPasswordChangeComplexity = (value) => {
  const password = String(value || '');
  return (
    password.length >= 8 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /\d/.test(password)
  );
};

const formatChallengeExpiryLabel = (value) => {
  if (!value) {
    return 'quelques minutes';
  }
  try {
    const expiry = new Date(value);
    return expiry.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch (error) {
    return 'quelques minutes';
  }
};

const ChangePasswordCard = ({ onPasswordChanged, twoFactorState }) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [pendingChallenge, setPendingChallenge] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isTwoFactorEnabled = Boolean(twoFactorState?.two_factor_enabled);
  const challengeExpiryLabel = useMemo(
    () => formatChallengeExpiryLabel(pendingChallenge?.expires_at),
    [pendingChallenge]
  );

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtpCode('');
    setPendingChallenge(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Renseignez les trois champs du formulaire.');
      return;
    }

    if (!hasPasswordChangeComplexity(newPassword)) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caracteres, une majuscule, une minuscule et un chiffre.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('La confirmation du nouveau mot de passe ne correspond pas.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/account/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      const responseData = response.data?.data || {};
      if (responseData.two_factor_required) {
        setPendingChallenge(responseData.challenge || null);
        setOtpCode('');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        toast.success(response.data?.message || 'Code OTP envoye par email.');
      } else {
        resetForm();
        toast.success(response.data?.message || 'Mot de passe mis a jour.');
        onPasswordChanged?.(responseData);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de mettre a jour le mot de passe.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!pendingChallenge?.challenge_id || !otpCode) {
      toast.error('Saisissez le code OTP recu par email.');
      return;
    }

    setVerifyingOtp(true);
    try {
      const response = await axios.post(`${API}/account/2fa/verify`, {
        challenge_id: pendingChallenge.challenge_id,
        code: otpCode,
      });
      const responseData = response.data?.data || {};
      resetForm();
      toast.success(response.data?.message || 'Verification reussie.');
      if (responseData.password_changed) {
        onPasswordChanged?.(responseData);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de verifier le code OTP.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingChallenge?.challenge_id) {
      toast.error("Aucune verification n est en attente.");
      return;
    }

    setResendingOtp(true);
    try {
      const response = await axios.post(`${API}/account/2fa/challenge`, {
        purpose: 'change_password',
        challenge_id: pendingChallenge.challenge_id,
      });
      setPendingChallenge(response.data?.data?.challenge || pendingChallenge);
      setOtpCode('');
      toast.success(response.data?.message || 'Nouveau code OTP envoye.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de renvoyer le code OTP.");
    } finally {
      setResendingOtp(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-red-600" />
          Securite
        </CardTitle>
        <CardDescription>
          Changez votre mot de passe depuis votre session active sans remplacer le flow de recuperation existant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-900">Niveau actuel</p>
          <p className="mt-2 text-sm text-slate-600">
            {isTwoFactorEnabled
              ? 'Cette action est protegee par un code OTP email supplementaire avant validation finale.'
              : 'Cette action est sensible. Vous pouvez activer la verification en deux etapes pour ajouter un OTP email.'}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <PasswordField
            id="account_current_password"
            dataTestId="change-password-current"
            label="Mot de passe actuel"
            value={currentPassword}
            onChange={setCurrentPassword}
            visible={showCurrentPassword}
            onToggleVisibility={() => setShowCurrentPassword((value) => !value)}
            disabled={Boolean(pendingChallenge)}
          />
          <PasswordField
            id="account_new_password"
            dataTestId="change-password-new"
            label="Nouveau mot de passe"
            value={newPassword}
            onChange={setNewPassword}
            visible={showNewPassword}
            onToggleVisibility={() => setShowNewPassword((value) => !value)}
            hint={passwordRuleItems.join(' · ')}
            disabled={Boolean(pendingChallenge)}
          />
          <PasswordField
            id="account_confirm_password"
            dataTestId="change-password-confirm"
            label="Confirmer le nouveau mot de passe"
            value={confirmPassword}
            onChange={setConfirmPassword}
            visible={showConfirmPassword}
            onToggleVisibility={() => setShowConfirmPassword((value) => !value)}
            disabled={Boolean(pendingChallenge)}
          />

          {pendingChallenge && (
            <div className="space-y-4 rounded-xl border border-red-200 bg-red-50 p-4">
              <div>
                <p className="text-sm font-medium text-slate-900">Code OTP requis</p>
                <p className="mt-1 text-sm text-slate-700">
                  Un code temporaire a ete envoye sur votre email verifie. Saisissez-le avant {challengeExpiryLabel}.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="account_change_password_otp">Code OTP</Label>
                <Input
                  id="account_change_password_otp"
                  data-testid="change-password-otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(event) => setOtpCode(event.target.value)}
                  placeholder="123456"
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" disabled={resendingOtp} onClick={handleResendOtp}>
                  {resendingOtp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                  {resendingOtp ? 'Renvoi...' : 'Renvoyer le code'}
                </Button>
                <Button
                  type="button"
                  className="bg-red-500 hover:bg-red-600"
                  disabled={verifyingOtp}
                  onClick={handleVerifyOtp}
                >
                  {verifyingOtp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                  {verifyingOtp ? 'Verification...' : 'Valider le code OTP'}
                </Button>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-700">
            <p className="font-medium text-slate-900">Besoin de recuperer l acces ?</p>
            <p className="mt-1">
              Si vous avez perdu votre mot de passe actuel, utilisez plutot le lien de reinitialisation securise.
            </p>
            <div className="mt-3">
              <Button type="button" variant="outline" onClick={() => navigate('/forgot-password')}>
                <KeyRound className="mr-2 h-4 w-4" />
                Utiliser le lien de reinitialisation
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" className="bg-red-500 hover:bg-red-600" disabled={submitting || Boolean(pendingChallenge)}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
              {submitting ? 'Mise a jour...' : pendingChallenge ? 'Code OTP en attente' : 'Mettre a jour le mot de passe'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const PasswordField = ({
  id,
  dataTestId,
  label,
  value,
  onChange,
  visible,
  onToggleVisibility,
  hint,
  disabled,
}) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input
        id={id}
        data-testid={dataTestId}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        disabled={disabled}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

export default ChangePasswordCard;
